'use client'
import { ComponentType, FC, PropsWithChildren } from 'react'
import { AppLayout, RefundCard } from '@/root/modules/tuuleliinid'
import { BookingContainer, InfoMessageContainer } from '@/root/components/tuuleliinid'
import { useCustomRouter, useGetTranslations } from '@/root/shared-hooks'
import type { TicketPageProps } from '@/app/[locale]/(routes)/ticket/[reservationToken]/ticket-page-shared'
import { useRefund } from '@/root/shared-hooks/refund/useRefund'
import { Loader } from '@/root/ui/shared'
import { Banner } from '@/root/ui/shared/Banner/Banner'
import { TitleText } from '@/root/modules/tuuleliinid/TicketPage/TitleText'

type TuuleliinidRefundPageProps = TicketPageProps & {
  /** The page frame. The TL header and footer by default; the trial theme passes its unbranded one. */
  Layout?: ComponentType<PropsWithChildren>
}

// Stable reference: React calls it only when the banner mounts, not on every re-render
const scrollIntoViewOnMount = (node: HTMLDivElement | null) => node?.scrollIntoView({ block: 'start' })

export const RefundPage: FC<TuuleliinidRefundPageProps> = ({ reservation: staticReservation, Layout = AppLayout }) => {
  const { getTranslation } = useGetTranslations()

  const { refundProps, isLoading, error } = useRefund(staticReservation)
  const { push, locale = 'en' } = useCustomRouter()

  if (error) {
    return (
      <Layout>
        <BookingContainer>
          <div className="flex justify-center py-8">
            <InfoMessageContainer
              body={getTranslation('ticketReservationNotFoundError')}
              ButtonText={getTranslation('ticketReservationNotFoundButton')}
              onClick={() => push(`/${locale}`)}
            />
          </div>
        </BookingContainer>
      </Layout>
    )
  }

  if (isLoading) {
    return (
      <Layout>
        <BookingContainer>
          <div className="flex justify-center py-40">
            <Loader />
          </div>
        </BookingContainer>
      </Layout>
    )
  }

  if (refundProps) {
    return (
      <Layout>
        <BookingContainer>
          {refundProps.actionButtonProps.hasCancelError && (
            // Confirm sits at the bottom of the card: bring the error into view when it appears
            <div ref={scrollIntoViewOnMount} className="mt-8">
              <Banner role="alert" variant="error" body={getTranslation('refundCancelError')} />
            </div>
          )}
          <TitleText className="my-8">
            {getTranslation('cancelAndRemoveTicket')}: {refundProps.reservationId}
          </TitleText>
          <RefundCard {...refundProps} />
        </BookingContainer>
      </Layout>
    )
  }
  return null
}

export default RefundPage
